import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import TemplateEditorDialog from "./TemplateEditorDialog";
import ConfirmDialog from "./ConfirmDialog";
import { deleteTemplate, getApiError } from "./emailApi";

const TemplateManagerDialog = ({
  open,
  onClose,
  templates,
  loading,
  error,
  onRetry,
  currentSession,
  onSaved,
  onDeleted,
}) => {
  const [editor, setEditor] = useState({ open: false, template: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const closeDelete = () => {
    setDeleteTarget(null);
    setDeleteError("");
  };

  const confirmDelete = async () => {
    try {
      setDeleting(true);
      setDeleteError("");
      await deleteTemplate(currentSession, deleteTarget.id);
      onDeleted(deleteTarget.id);
      closeDelete();
    } catch (e) {
      setDeleteError(getApiError(e, "Failed to delete template."));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Email templates</DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress color="warning" />
            </Box>
          ) : error ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={onRetry}>
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          ) : templates.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No templates yet. Create your first one.
            </Typography>
          ) : (
            <List disablePadding>
              {templates.map((t) => (
                <ListItem
                  key={t.id}
                  divider
                  secondaryAction={
                    <>
                      <Tooltip title="Edit">
                        <IconButton
                          onClick={() => setEditor({ open: true, template: t })}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          color="error"
                          onClick={() => setDeleteTarget(t)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  }
                >
                  <ListItemText
                    primary={t.name}
                    secondary={t.subject}
                    slotProps={{ secondary: { noWrap: true } }}
                    sx={{ pr: 10 }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<AddIcon />}
            onClick={() => setEditor({ open: true, template: null })}
          >
            New template
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <TemplateEditorDialog
        open={editor.open}
        template={editor.template}
        currentSession={currentSession}
        onClose={() => setEditor({ open: false, template: null })}
        onSaved={(saved) => {
          onSaved(saved);
          setEditor({ open: false, template: null });
        }}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete template?"
        message={`"${deleteTarget?.name || ""}" will be permanently deleted for all admins. This can't be undone.`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleting}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={closeDelete}
      />
    </>
  );
};

export default TemplateManagerDialog;
